import React from 'react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { formatPhoneNumber } from '../../pages/ClientIntake';
import { PersonInput } from './PersonSelect';

export default function HealthcareStep({ form, updateForm, addPersonToList }) {
    const update = (f, v) => updateForm({ healthcare_directive: { ...form.healthcare_directive, [f]: v } });
    const el = React.createElement;

    return el('div', { className: 'space-y-8' },
        el('div', { className: 'p-4 border border-stone-200 space-y-4' },
            el('h4', { className: 'font-serif text-lg' }, 'Healthcare Preferences'),
            el('div', { className: 'flex flex-wrap gap-6' },
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { id: 'has_living_will', checked: form.healthcare_directive.has_living_will, onCheckedChange: c => update('has_living_will', c) }),
                    el(Label, { htmlFor: 'has_living_will' }, 'Has Existing Living Will')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { id: 'dnr_order', checked: form.healthcare_directive.dnr_order, onCheckedChange: c => update('dnr_order', c) }),
                    el(Label, { htmlFor: 'dnr_order' }, 'DNR Order')
                ),
                el('div', { className: 'flex items-center space-x-2' },
                    el(Checkbox, { id: 'organ_donor', checked: form.healthcare_directive.organ_donor, onCheckedChange: c => update('organ_donor', c) }),
                    el(Label, { htmlFor: 'organ_donor' }, 'Organ Donor')
                )
            )
        ),
        
        // Healthcare Agent Section
        el('div', { className: 'p-4 border border-stone-200 space-y-4' },
            el('h4', { className: 'font-serif text-lg' }, 'Healthcare Agent'),
            el('p', { className: 'text-sm text-muted-foreground' }, 'Person authorized to make healthcare decisions'),
            el('div', { className: 'grid grid-cols-1 md:grid-cols-4 gap-4' },
                el('div', { className: 'space-y-2 col-span-2' },
                    el(Label, null, 'Agent Name'),
                    el(PersonInput, { 
                        value: form.healthcare_directive.healthcare_agent_name, 
                        onChange: (val) => update('healthcare_agent_name', val), 
                        onAddPerson: addPersonToList,
                        people: form.people || [],
                        spouse: form.spouse,
                        children: form.children,
                        placeholder: 'Select or type name',
                        relationship: 'healthcare agent'
                    })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Pronouns'),
                    el('select', { 
                        value: form.healthcare_directive.healthcare_agent_gender || '', 
                        onChange: e => update('healthcare_agent_gender', e.target.value),
                        className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none'
                    },
                        el('option', { value: '' }, 'Select...'),
                        el('option', { value: 'male' }, 'He/Him'),
                        el('option', { value: 'female' }, 'She/Her'),
                        el('option', { value: 'neutral' }, 'They/Them')
                    )
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Relationship'),
                    el(Input, { value: form.healthcare_directive.healthcare_agent_relationship, onChange: e => update('healthcare_agent_relationship', e.target.value), className: 'rounded-none', placeholder: 'e.g., spouse, daughter' })
                )
            ),
            el('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Phone'),
                    el(Input, { type: 'tel', value: form.healthcare_directive.healthcare_agent_phone, onChange: e => update('healthcare_agent_phone', formatPhoneNumber(e.target.value)), className: 'rounded-none', placeholder: '(555) 555-5555' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Street Address'),
                    el(Input, { value: form.healthcare_directive.healthcare_agent_address || '', onChange: e => update('healthcare_agent_address', e.target.value), className: 'rounded-none', placeholder: '123 Main Street' })
                )
            ),
            el('div', { className: 'grid grid-cols-1 md:grid-cols-4 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'City'),
                    el(Input, { value: form.healthcare_directive.healthcare_agent_city || '', onChange: e => update('healthcare_agent_city', e.target.value), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'County'),
                    el(Input, { value: form.healthcare_directive.healthcare_agent_county || '', onChange: e => update('healthcare_agent_county', e.target.value), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'State'),
                    el(Input, { value: form.healthcare_directive.healthcare_agent_state || '', onChange: e => update('healthcare_agent_state', e.target.value), className: 'rounded-none', placeholder: 'Virginia' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'ZIP'),
                    el(Input, { value: form.healthcare_directive.healthcare_agent_zip || '', onChange: e => update('healthcare_agent_zip', e.target.value), className: 'rounded-none' })
                )
            )
        ),
        
        // Alternate Healthcare Agent Section
        el('div', { className: 'p-4 border border-stone-200 space-y-4' },
            el('h4', { className: 'font-serif text-lg' }, 'Alternate Healthcare Agent'),
            el('p', { className: 'text-sm text-muted-foreground' }, 'Backup person if primary agent is unavailable'),
            el('div', { className: 'grid grid-cols-1 md:grid-cols-4 gap-4' },
                el('div', { className: 'space-y-2 col-span-2' },
                    el(Label, null, 'Alternate Agent Name'),
                    el(PersonInput, { 
                        value: form.healthcare_directive.alternate_agent_name, 
                        onChange: (val) => update('alternate_agent_name', val), 
                        onAddPerson: addPersonToList,
                        people: form.people || [],
                        spouse: form.spouse,
                        children: form.children,
                        placeholder: 'Select or type name',
                        relationship: 'alternate healthcare agent'
                    })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Pronouns'),
                    el('select', { 
                        value: form.healthcare_directive.alternate_agent_gender || '', 
                        onChange: e => update('alternate_agent_gender', e.target.value),
                        className: 'w-full h-10 px-3 border border-stone-200 bg-white rounded-none'
                    },
                        el('option', { value: '' }, 'Select...'),
                        el('option', { value: 'male' }, 'He/Him'),
                        el('option', { value: 'female' }, 'She/Her'),
                        el('option', { value: 'neutral' }, 'They/Them')
                    )
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Relationship'),
                    el(Input, { value: form.healthcare_directive.alternate_agent_relationship || '', onChange: e => update('alternate_agent_relationship', e.target.value), className: 'rounded-none', placeholder: 'e.g., son, friend' })
                )
            ),
            el('div', { className: 'grid grid-cols-1 md:grid-cols-2 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Phone'),
                    el(Input, { type: 'tel', value: form.healthcare_directive.alternate_agent_phone, onChange: e => update('alternate_agent_phone', formatPhoneNumber(e.target.value)), className: 'rounded-none', placeholder: '(555) 555-5555' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'Street Address'),
                    el(Input, { value: form.healthcare_directive.alternate_agent_address || '', onChange: e => update('alternate_agent_address', e.target.value), className: 'rounded-none', placeholder: '456 Oak Avenue' })
                )
            ),
            el('div', { className: 'grid grid-cols-1 md:grid-cols-4 gap-4' },
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'City'),
                    el(Input, { value: form.healthcare_directive.alternate_agent_city || '', onChange: e => update('alternate_agent_city', e.target.value), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'County'),
                    el(Input, { value: form.healthcare_directive.alternate_agent_county || '', onChange: e => update('alternate_agent_county', e.target.value), className: 'rounded-none' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'State'),
                    el(Input, { value: form.healthcare_directive.alternate_agent_state || '', onChange: e => update('alternate_agent_state', e.target.value), className: 'rounded-none', placeholder: 'Virginia' })
                ),
                el('div', { className: 'space-y-2' },
                    el(Label, null, 'ZIP'),
                    el(Input, { value: form.healthcare_directive.alternate_agent_zip || '', onChange: e => update('alternate_agent_zip', e.target.value), className: 'rounded-none' })
                )
            )
        ),
        
        // Special Instructions
        el('div', { className: 'p-4 border border-stone-200 space-y-4' },
            el('h4', { className: 'font-serif text-lg' }, 'Special Healthcare Instructions'),
            el(Textarea, { value: form.healthcare_directive.special_instructions, onChange: e => update('special_instructions', e.target.value), className: 'rounded-none min-h-[100px]', placeholder: 'Any specific healthcare wishes or instructions...' })
        )
    );
}
